import { c as createFocusTrap } from './focus-trap.esm-BUj_YYcQ.js';
import { onClickOutside } from '@utils';
import { pubsub } from '@services';

class ContentSelect extends HTMLElement {
  #button;
  #list;
  #hiddenInput;
  #options;
  #handleResize = () => this.#updatePosition();
  #handleScroll = () => this.#updatePosition();
  #trap;
  #unsubscribeEscapeKey;

  connectedCallback() {
    // Initialize element references after DOM is connected
    this.#button = this.querySelector('[data-select-button]');
    this.#list = this.querySelector('[data-select-list]');
    this.#hiddenInput = this.querySelector('[data-select-input]');
    this.#options = this.#list?.querySelectorAll('[role="menuitem"]');
    this.#trap = createFocusTrap(this, { allowOutsideClick: true });

    if (!this.#button || !this.#list) {
      console.error('ContentSelect: Required elements not found');
      return;
    }

    this.#button.addEventListener('click', () => this.#toggle());
    this.#list.addEventListener('click', (event) => {
      const option = event.target.closest('[data-value]');
      if (option) this.#selectOption(option);
    });
    this.#list.addEventListener('toggle', this.#handleToggle.bind(this));

    this.#unsubscribeEscapeKey = pubsub.subscribe('key:escape', () => this.#close());
    onClickOutside(this, () => this.#close());

    this.#setInitialPosition();
    this.#initSelectedOption();
  }

  disconnectedCallback() {
    this.#unsubscribeEscapeKey?.();
    if (this.#handleResize) {
      window.removeEventListener('resize', this.#handleResize);
      window.removeEventListener('scroll', this.#handleScroll, true);
    }
  }

  #setInitialPosition() {
    const rect = this.#button.getBoundingClientRect();

    Object.assign(this.#list.style, {
      position: 'fixed',
      left: `${rect.left}px`,
      width: `${rect.width}px`,
    });

    this.#calculateVerticalPosition(rect);
  }

  #calculateVerticalPosition(rect) {
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 320;
    const spacing = 8;
    const spaceBelow = viewportHeight - rect.bottom - spacing;
    const spaceAbove = rect.top - spacing;

    const showBelow = spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove;

    Object.assign(this.#list.style, {
      top: showBelow ? `${rect.bottom + spacing}px` : 'auto',
      bottom: showBelow ? 'auto' : `${viewportHeight - rect.top + spacing}px`,
    });

    this.#list.classList.toggle('origin-top', showBelow);
    this.#list.classList.toggle('origin-bottom', !showBelow);
  }

  #initSelectedOption() {
    let selectedOption = this.#list.querySelector('[data-selected]');

    if (!selectedOption && this.#hiddenInput?.value) {
      selectedOption = Array.from(this.#options).find(
        (option) => option.dataset.value === this.#hiddenInput.value
      );
      selectedOption?.setAttribute('data-selected', '');
    }

    if (!selectedOption) return;

    this.#updateButtonContent(selectedOption);
    if (this.#hiddenInput) {
      this.#hiddenInput.value = selectedOption.dataset.value;
    }
  }

  #updateButtonContent(optionElement) {
    if (!optionElement) return;
    this.#button.innerHTML = optionElement.cloneNode(true).innerHTML;
  }

  #isPopoverOpen() {
    return this.#list.matches(':popover-open') || this.#button.ariaExpanded === 'true';
  }

  #updatePosition() {
    const rect = this.#button.getBoundingClientRect();

    Object.assign(this.#list.style, {
      position: 'fixed',
      left: `${rect.left}px`,
      width: `${rect.width}px`,
    });

    this.#calculateVerticalPosition(rect);
  }

  #toggle() {
    this.#isPopoverOpen() ? this.#close() : this.#open();
  }

  #open() {
    if (this.#isPopoverOpen()) return;

    this.#updatePosition();
    this.#list.showPopover();
    this.#button.ariaExpanded = 'true';

    requestAnimationFrame(() => {
      this.#list.classList.remove('opacity-0', 'scale-95');
      this.#list.classList.add('opacity-100', 'scale-100');
    });

    window.addEventListener('resize', this.#handleResize);
    window.addEventListener('scroll', this.#handleScroll, true);
    this.#trap.activate();
  }

  #close() {
    if (!this.#isPopoverOpen()) return;

    this.#list.classList.remove('opacity-100', 'scale-100');
    this.#list.classList.add('opacity-0', 'scale-95');

    setTimeout(() => {
      if (this.#isPopoverOpen()) this.#list.hidePopover();
    }, 200);

    window.removeEventListener('resize', this.#handleResize);
    window.removeEventListener('scroll', this.#handleScroll, true);
    this.#trap.deactivate();
    this.#button.ariaExpanded = 'false';
  }

  #handleToggle(event) {
    if (event.newState !== 'closed') return;

    this.#button.ariaExpanded = 'false';
    this.#list.classList.remove('opacity-100', 'scale-100');
    this.#list.classList.add('opacity-0', 'scale-95');
    window.removeEventListener('resize', this.#handleResize);
    window.removeEventListener('scroll', this.#handleScroll, true);
    this.#trap.deactivate();
  }

  #selectOption(optionElement) {
    this.#options.forEach((option) => option.removeAttribute('data-selected'));
    if (!optionElement || !this.#hiddenInput) return;

    optionElement.setAttribute('data-selected', '');
    this.#updateButtonContent(optionElement);
    this.#updateHiddenInput(optionElement);
    this.#close();
  }

  #updateHiddenInput(optionElement) {
    this.#hiddenInput.value = optionElement.dataset.value;
    this.#hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

customElements.define('content-select', ContentSelect);
